declare var jQuery: any, llCategories: string[], llLanguageMode: string;

class Product {
  Id?: number = 0;
  Name: string = "";
  Link: string = "";
  Quantity: number = 1;
  UnitNetPrice: number = 0;
  UnitGrossPrice: number = 0;
  Tax: number = 0;
  UnitTaxValue: number = 0;
  CategoryLevel1: string = "";
  CategoryLevel2: string = "";
  CategoryLevel3: string = "";

  static fromJson(json: any, quantity = 1): Product {
    const gross = json.price.gross.final_float;
    const net = json.price.net.final_float;
    const taxValue = gross - net;
    const taxRate = Math.round((taxValue / net) * 100);

    const product = new Product();
    product.Name = json.name;
    product.Link = json.url;
    product.Quantity = quantity;
    product.UnitNetPrice = json.price.net.final_float;
    product.UnitGrossPrice = json.price.gross.final_float;
    product.Tax = taxRate;
    product.UnitTaxValue = taxValue;
    product.CategoryLevel1 = json.category.name;
    product.CategoryLevel2 = "";
    product.CategoryLevel3 = "";

    return product;
  }
}

class CalculatorOnsite {
  static readonly MIN_NET_VALUE = 300;
  static readonly MAX_NET_VALUE = 150000;
  scrpitElementSrc =
    "https://elements.leaselink.pl/leaselinkelements/leaselinkelements.esm.js";
  calculatorElement!: HTMLElement;
  parentElement: HTMLElement | null = null;
  apiKey?: string;
  productEnabled: boolean = false;
  basketEnabled: boolean = false;
  disableProcess: boolean = false;
  disableBuildCart: boolean = false;
  testEnvironment: boolean = false;
  dynamicPrice: string | null = null;
  renderToProductSelector: string = ".product-main-box .basket";
  renderToBasketSelector: string = "table.productlist";
  currency: string = "pl_PL";
  buttonLabel: string = "Oblicz ratę w Leasingu";
  buttonStyle: string = "";
  buttonRadius: string = "";
  mode?: "product" | "basket";
  productId = 0;
  loggerMode = false;
  logs: any[] = [];
  private categories: string[] = typeof llCategories !== "undefined" ? llCategories : [];

  constructor() {
    this.init();
    this.logger("CalculatorOnsite class initialized", this);
  }

  private async init() {
    const getCurrentScript = (): Promise<HTMLScriptElement> => {
      return new Promise((resolve) => {
        const checkScript = () => {
          if (document.currentScript) {
            resolve(document.currentScript as HTMLScriptElement);
          } else {
            setTimeout(checkScript, 100);
          }
        };
        checkScript();
      });
    };

    try {
      const currentScript = await getCurrentScript();
      const dataset = currentScript.dataset;

      this.apiKey = dataset?.apiKey || undefined;
      this.disableProcess = dataset?.disableProcess === "1";
      this.disableBuildCart = dataset?.disableBuildCart === "1";
      this.testEnvironment = dataset?.testEnvironment === "1";
      this.buttonLabel = dataset?.buttonLabel || this.buttonLabel;
      this.buttonStyle = dataset?.buttonStyle || this.buttonStyle;
      this.buttonRadius = dataset?.buttonRadius || this.buttonRadius;
      this.productId = parseInt(dataset?.productId || "0");
      this.productEnabled = dataset?.productEnabled === "1";
      this.basketEnabled = dataset?.basketEnabled === "1";
      this.renderToProductSelector =
        dataset?.renderToProduct || this.renderToProductSelector;
      this.renderToBasketSelector =
        dataset?.renderToBasket || this.renderToBasketSelector;

      this.detectPageType();
      await this.detectFrontAPI();

      if (this.isAvailable()) {
        this.logger("is available");
        await this.loadElementsScript();
        this.initCalculatorElement();
        this.addCalculatorElementToPage();
      }

      if (document.getElementById("finalize-order-form")) {
        await this.loadElementsScript();
        this.initFinalizeOrderElement();
      }
    } catch (error) {
      this.logger("Error loading script", error);
    }
  }

  getProductById(id: number) {
    return (window as any).frontAPI.getProduct({
      currency: this.currency,
      id: id,
    });
  }

  checkVisibilityOnProductByCategory() {
    const product = this.getProductById(this.productId);
    return this.isInAllowedCategories(product);
  }

  private isInAllowedCategories(product: any) {
    if (this.categories.length === 0) {
      return true;
    }

    const productCategoryId = product.category?.id ?? product.category?.category_id;

    if (productCategoryId == null) return true;

    if(this.categories.indexOf(productCategoryId.toString()) !== -1) {
      return true;
    }

    const allCategories = (window as any).frontAPI.getCategories();

    const categoryPath = CalculatorOnsite.findCategoryPath(
      allCategories,
      productCategoryId
    );

    for (const category of categoryPath) {
      if(this.categories.indexOf(category.id) !== -1) {
        return true;
      }
    }

    return false;
  }

  checkVisibilityOnBasketByCategory(basket: any) {
    return basket.products.every((product: any) => {
      return this.isInAllowedCategories(product);
    });
  }

  initFinalizeOrderElement() {
    this.logger("Initializing finalize order element");

    const finalizeOrderElement = (window as any)
      .leaselinkElementsBuilder("div")
      .api({
        apiKey: this.apiKey,
      })
      .styles({
        externalId: this.buttonStyle,
      })
      .button({
        label: "PRZEJDŹ DO WNIOSKU",
      })
      .calculator({
        disableProcess: this.disableProcess,
      })
      .build();

    this.setTestEnvironment(finalizeOrderElement);

    const button =
      finalizeOrderElement.getElementsByTagName("leaselink-button")[0];
    button.addEventListener("click", () => {
      const form = document.getElementById(
        "finalize-order-form"
      ) as HTMLFormElement;
      if (form) {
        form.submit();
      }
    });

    document
      .getElementById("finalize-order-form")
      ?.appendChild(finalizeOrderElement);
  }

  initCalculatorElement() {
    this.logger("Initializing calculator element");

    this.calculatorElement = (window as any)
      .leaselinkElementsBuilder("div")
      .api({
        apiKey: this.apiKey,
      })
      .styles({
        externalId: this.buttonStyle,
      })
      .button({
        label: this.buttonLabel,
      })
      .calculator({
        disableProcess: this.disableProcess,
      })
      .build();

    this.setTestEnvironment(this.calculatorElement);

    this.calculatorElement.style.marginTop = "20px";
  }

  // Tryb testowy LeaseLink - builder nie przekazuje tej flagi, ustawiamy atrybut ręcznie
  // zaraz po .build(), zanim element trafi do DOM. Na produkcji atrybutu nie ustawiamy wcale.
  private setTestEnvironment(element: HTMLElement) {
    if (!this.testEnvironment) {
      return;
    }
    element
      .getElementsByTagName("leaselink-api")[0]
      ?.setAttribute("test-environment", "true");
  }

  clearCart() {
    (window as any).leaselinkApi.clearCart();
  }

  show() {
    (window as any).leaselinkApi.showCalculator();
  }

  onClickedButton() {
    if (this.disableBuildCart || this.mode === "basket") {
      this.clearCart();
    }
    if (this.mode === "product") {
      this.addItemToCartByProductId(this.productId);
    } else if (this.mode === "basket") {
      this.addItemToCartByBasket();
    }
    this.show();
  }

  /**
   * Component-Placement raportowany do LeaseLink dla bieżącej strony.
   */
  get componentPlacement(): string {
    if (this.mode === "product") return "card";
    if (this.mode === "basket") return "summary";
    return "payments";
  }

  addItemToCart(item: Product) {
    (window as any).leaselinkApi.addItemToCart(item, {
      disableDefault: true,
      apiUsageData: {
        componentName: "onsite",
        componentPlacement: this.componentPlacement,
        externalApp: "Shoper",
      },
    });
  }

  static findCategoryPath(categories: any[], targetId: number | string): { id: string, name: string }[] {
    const normalizedTarget = targetId.toString();
    for (const category of categories) {
      if (category.id.toString() === normalizedTarget) {
        return [{ id: category.id.toString(), name: category.name }];
      }

      if (category.children && category.children.length > 0) {
        const path = CalculatorOnsite.findCategoryPath(
          category.children,
          targetId
        );
        if (path.length > 0) {
          return [{ id: category.id.toString(), name: category.name }, ...path];
        }
      }
    }
    return [];
  }

  addItemToCartByProductId(productId: number, quantity: number = 1) {

    const productOption = this.getProductOptions();
    
    const product = this.getProduct(productId, productOption);

    const categories = (window as any).frontAPI.getCategories();

    const categoryPath = CalculatorOnsite.findCategoryPath(
      categories,
      product.category.id
    );

    const item: Product = Product.fromJson(product, 1);
    item.CategoryLevel1 = categoryPath[0]?.name || "";
    item.CategoryLevel2 = categoryPath[1]?.name || "";
    item.CategoryLevel3 = categoryPath[2]?.name || "";
    
    this.addItemToCart(item);
  }

  addItemToCartByBasket() {
    const basket = (window as any).frontAPI.getBasketInfo({
      currency: this.currency,
    });

    // Unikalne ID produktów tylko do pobrania szczegółów/kategorii.
    const uniqueProductIds = [
      ...new Set(basket.products.map((p: any) => p.product_id)),
    ];

    const products = (window as any).frontAPI.getProducts({
      currency: this.currency,
      ids: uniqueProductIds,
    });

    // Mapa szczegółów produktów po ID dla szybkiego dostępu.
    const productsDetailsMap = new Map();
    products.list.forEach((product: any) => {
      productsDetailsMap.set(product.id, product);
    });

    const categories = (window as any).frontAPI.getCategories();

    // Iteruj po KAŻDEJ pozycji koszyka (ten sam produkt w różnych wariantach
    // to osobne pozycje o tym samym product_id) - inaczej do kalkulatora
    // trafiałaby tylko jedna z nich.
    basket.products.forEach((basketProduct: any) => {
      const product = productsDetailsMap.get(basketProduct.product_id);

      if (!product) {
        console.warn(
          `Product details not found for product_id: ${basketProduct.product_id}`
        );
        return;
      }

      // Oblicz stawkę VAT z oryginalnego produktu
      const vatRate = (product.price.gross.final_float - product.price.net.final_float) / product.price.net.final_float;

      // Użyj ceny z koszyka (zawiera warianty), nie bazowej ceny produktu
      const grossPriceWithVariant = basketProduct.price_float;
      const netPriceWithVariant = grossPriceWithVariant / (1 + vatRate);

      const productWithBasketPrice = {
        ...product,
        price: {
          gross: {
            final_float: grossPriceWithVariant,
            final: basketProduct.price
          },
          net: {
            final_float: netPriceWithVariant,
            final: netPriceWithVariant.toFixed(2)
          }
        }
      };

      const item: Product = Product.fromJson(
        productWithBasketPrice,
        basketProduct.quantity
      );
      const categoryPath = CalculatorOnsite.findCategoryPath(
        categories,
        product.category.id
      );
      item.CategoryLevel1 = categoryPath[0]?.name || "";
      item.CategoryLevel2 = categoryPath[1]?.name || "";
      item.CategoryLevel3 = categoryPath[2]?.name || "";

      this.addItemToCart(item);
    });
  }

  addCalculatorElementToPage() {
    this.logger("Adding calculator element to page");
    if (!this.parentElement || !this.calculatorElement) {
      return;
    }
    if (this.mode === "product") {
      if (this.parentElement.parentNode) {
        this.parentElement.parentNode.insertBefore(
          this.calculatorElement,
          this.parentElement.nextSibling
        );
        this.logger("Inserting calculator element to basket page");
      }
      this.logger("Appending calculator element to product page");
    } else if (this.mode === "basket") {
      if (this.parentElement.parentNode) {
        this.parentElement.parentNode.insertBefore(
          this.calculatorElement,
          this.parentElement.nextSibling
        );
        this.logger("Inserting calculator element to basket page");
      }
    }

    const calculator =
      this.calculatorElement.getElementsByTagName("leaselink-calculator")[0];
    calculator.setAttribute("disable-process", this.disableProcess.toString());

    // Białe logo LeaseLink dla Stylu 1 (SHOPER-BUTTON-STYLE01).
    // Builder nie wystawia tej opcji, dlatego ustawiamy atrybut bezpośrednio na elemencie.
    if (this.buttonStyle === "SHOPER-BUTTON-STYLE01") {
      calculator.setAttribute("white-logo", "true");
    }

    const button =
      this.calculatorElement.getElementsByTagName("leaselink-button")[0];
    button.addEventListener("click", () => {
      this.onClickedButton();
    });

    // Zaokrąglenie rogów przycisku CTA. Zmienna CSS wystawiana przez elementy
    // LeaseLink dziedziczy przez granicę shadow DOM. Ustawiamy tylko na hoście
    // przycisku, aby nie zmieniać wyglądu przycisków wewnątrz modala kalkulatora.
    const radius = parseInt(this.buttonRadius, 10);
    if (!isNaN(radius)) {
      (button as HTMLElement).style.setProperty(
        "--leaselink-button-border-radius",
        `${radius}px`
      );
    }
  }

  isAvailable(): boolean {
    // Sprawdź dostępność językową
    if (!this.isLanguageAvailable()) {
      return false;
    }

    if (this.mode === "product" && this.productEnabled) {
      if (!this.checkVisibilityOnProductByCategory()) {
        return false;
      }

      if (!this.disableBuildCart) {
        return true;
      }

      const product = (window as any).frontAPI.getProduct({
        currency: this.currency,
        id: this.productId,
      });

      if (!product.can_buy) {
        return false;
      }

      return (
        product.price.net.final_float >= CalculatorOnsite.MIN_NET_VALUE &&
        product.price.net.final_float <= CalculatorOnsite.MAX_NET_VALUE
      );
    } else if (this.mode === "basket" && this.basketEnabled) {
      const basket = (window as any).frontAPI.getBasketInfo({
        currency: this.currency,
      });

      if (!this.checkVisibilityOnBasketByCategory(basket)) {
        return false;
      }

      return (
        basket.basket.sum_products_float >= CalculatorOnsite.MIN_NET_VALUE &&
        basket.basket.sum_products_float <= CalculatorOnsite.MAX_NET_VALUE
      );
    }

    return false;
  }

  isLanguageAvailable(): boolean {
    if (typeof llLanguageMode === "undefined" || llLanguageMode === "all") {
      return true;
    }

    if (llLanguageMode === "pl_only") {
      if (typeof (window as any).frontAPI !== "undefined" && (window as any).frontAPI.getLang) {
        const currentLang = (window as any).frontAPI.getLang();
        return currentLang === "pl_PL";
      }
      return true; // Jeśli nie można pobrać języka, zakładamy dostępność
    }

    return true;
  }

  async detectFrontAPI() {
    return new Promise((resolve, reject) => {
      let interval = setInterval(() => {
        if ((window as any).frontAPI) {
          clearInterval(interval);
          resolve((window as any).frontAPI);
        }
      }, 500);
    });
  }

  private getProduct(productId: number, options?: any) {
    let product = (window as any).frontAPI.getProduct({
      currency: this.currency,
      id: productId,
    });
    if (options) {
      const productOption = frontAPI.getProductOption({
        id: this.productId.toString(),
        data: {
          options,
        },
      });

      product = Object.assign(product, productOption);
    }

    return product;
  }

  private getProductOptions() {
    const form = document.querySelector("form.form-basket");
    if (!form) {
      return null;
    }

    const formElements = form.querySelectorAll("[id*='option_']");
    const formData: { [key: string]: any } = {};

    formElements.forEach((element) => {
      const inputElement = element as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement;

      const optionId = inputElement.id.replace("option_", "");
      formData[optionId] = inputElement.value;
    });

    return formData;
  }

  async loadElementsScript() {
    // Builder już gotowy — nic nie rób
    if (typeof (window as any).leaselinkElementsBuilder === "function") {
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${this.scrpitElementSrc}"]`
    );

    // Wstaw tag <script> tylko jeśli jeszcze go nie ma
    if (!existingScript) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.type = "module";
        script.src = this.scrpitElementSrc;
        script.async = true;

        script.addEventListener("load", () => resolve());
        script.addEventListener("error", (error) => reject(error));

        document.getElementsByTagName("head")[0].appendChild(script);
      });
    }

    // Obecność tagu <script> nie gwarantuje, że moduł ESM już się wykonał
    // i zarejestrował builder — poczekaj aż faktycznie będzie dostępny.
    await this.waitForElementsBuilder();
  }

  waitForElementsBuilder(timeoutMs = 10000, intervalMs = 50): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = performance.now();
      const check = () => {
        if (typeof (window as any).leaselinkElementsBuilder === "function") {
          resolve();
        } else if (performance.now() - start > timeoutMs) {
          reject(new Error("leaselinkElementsBuilder not available (timeout)"));
        } else {
          setTimeout(check, intervalMs);
        }
      };
      check();
    });
  }

  detectPageType() {
    if (document.querySelector("body.shop_product")) {
      this.mode = "product";
      this.parentElement = document.querySelector(this.renderToProductSelector);
    } else if (document.querySelector("body.shop_basket")) {
      this.mode = "basket";
      this.parentElement = document.querySelector(this.renderToBasketSelector);
    } else {
      this.mode = undefined;
      this.parentElement = null;
    }
  }

  logger(...message: any) {
    this.logs.push(message);
    if (this.loggerMode) {
      console.log(message);
    }
  }
}

(window as any).calculatorOnsite = new CalculatorOnsite();
